/**
 * PLOT PLANNER — collaboration store (client Firestore SDK)
 * ─────────────────────────────────────────────────────────
 * Step 1 of the collaboration feature (see docs/collab-sketch.md).
 *
 * The thin read/write layer for `plans/{planId}`. Unlike the single-owner
 * planner (which loads once via the getPlannerProgress callable and saves the
 * whole blob via savePlannerProgress), a collaborative plan is read LIVE with
 * onSnapshot and edited with SCOPED updateDoc writes — one field path per unit —
 * so two editors touching different fields never clobber each other.
 *
 * This module owns the only Firebase boundary for plans. It builds every write
 * payload from the pure path builders in planModel.js (P.*). It does NOT create
 * plans (createPlan is an Admin-SDK callable; rules deny client create) and does
 * NOT touch ownership/membership fields (invite/remove are callables). Every
 * write stamps `updatedAt` so the plan picker can sort by recency.
 *
 * Fine-grained writes (own field path): core.*, fields.*, cast.<id>.*,
 * rel.<id>.*, dynasty.links.<id>.*, shape.
 * Coarse writes (whole atom): a faction (its members[] are positional), an act's
 * beats (positional), dynasty.allegiance (house-name keys). See planModel.js.
 */
import { db } from '../firebase';
import {
  doc, onSnapshot, updateDoc, deleteField, arrayUnion, arrayRemove, serverTimestamp,
} from 'firebase/firestore';
import { P } from './planModel';
import { DELETE_SENTINEL } from './planSync';

const planRef = (planId) => doc(db, 'plans', planId);

/* Live subscription. onData(data|null), optional onError(err). Returns unsub. */
export function subscribePlan(planId, onData, onError) {
  return onSnapshot(
    planRef(planId),
    (snap) => onData(snap.exists() ? snap.data() : null),
    (err) => { if (onError) onError(err); },
  );
}

/* One-shot: apply a set of field-path updates, always stamping updatedAt. */
export function writePlanPaths(planId, updates) {
  return updateDoc(planRef(planId), { ...updates, updatedAt: serverTimestamp() });
}

/* Apply a diff produced by planSync.diffContentToWrites: plain values are set,
   the DELETE_SENTINEL becomes a Firestore deleteField(). Used by the collab
   editor to push only the paths that changed. */
export function applyContentDiff(planId, diff) {
  const updates = {};
  for (const path of Object.keys(diff)) {
    updates[path] = diff[path] === DELETE_SENTINEL ? deleteField() : diff[path];
  }
  return writePlanPaths(planId, updates);
}

/* ── core / fields / shape (fine-grained) ─────────────────────────────────── */
export function setCore(planId, key, value) {
  const updates = { [P.core(key)]: value };
  if (key === 'title') updates.title = value; // keep the denormalised picker title in sync
  return writePlanPaths(planId, updates);
}
export function setField(planId, fieldId, value) {
  return writePlanPaths(planId, { [P.field(fieldId)]: value });
}
export function setShape(planId, shape) {
  return writePlanPaths(planId, { [P.shape()]: shape });
}

/* ── cast (id-keyed map + castOrder) ──────────────────────────────────────── */
export function addCast(planId, item) {
  return writePlanPaths(planId, { [P.castItem(item.id)]: item, [P.castOrder()]: arrayUnion(item.id) });
}
export function setCastProp(planId, id, key, value) {
  return writePlanPaths(planId, { [P.castProp(id, key)]: value });
}
export function setCastField(planId, id, key, value) {
  return writePlanPaths(planId, { [P.castField(id, key)]: value });
}
/* Deleting a character also orphans any dynasty links that reference it. The
   caller (which holds current state) passes the affected link ids so we never
   need a server read to compute them. */
export function deleteCast(planId, id, { orphanLinkIds = [] } = {}) {
  const updates = { [P.castItem(id)]: deleteField(), [P.castOrder()]: arrayRemove(id) };
  orphanLinkIds.forEach((lid) => { updates[P.linkItem(lid)] = deleteField(); });
  if (orphanLinkIds.length) updates[P.linkOrder()] = arrayRemove(...orphanLinkIds);
  return writePlanPaths(planId, updates);
}

/* ── relationships (id-keyed map + relOrder) ──────────────────────────────── */
export function addRel(planId, item) {
  return writePlanPaths(planId, { [P.relItem(item.id)]: item, [P.relOrder()]: arrayUnion(item.id) });
}
export function setRelPair(planId, id, value) {
  return writePlanPaths(planId, { [P.relProp(id, 'pair')]: value });
}
export function setRelField(planId, id, key, value) {
  return writePlanPaths(planId, { [P.relField(id, key)]: value });
}
export function deleteRel(planId, id) {
  return writePlanPaths(planId, { [P.relItem(id)]: deleteField(), [P.relOrder()]: arrayRemove(id) });
}

/* ── factions (id-keyed map + factionOrder; each faction COARSE) ───────────── */
export function addFaction(planId, faction) {
  return writePlanPaths(planId, { [P.faction(faction.id)]: faction, [P.factionOrder()]: arrayUnion(faction.id) });
}
/* Coarse: any edit to a faction — its name/head OR any member row (members are
   positional, no ids) — rewrites the whole faction object. The caller passes the
   full, updated faction. */
export function setFaction(planId, faction) {
  return writePlanPaths(planId, { [P.faction(faction.id)]: faction });
}
export function deleteFaction(planId, id) {
  return writePlanPaths(planId, { [P.faction(id)]: deleteField(), [P.factionOrder()]: arrayRemove(id) });
}

/* ── dynasty links (id-keyed map + linkOrder) ─────────────────────────────── */
export function addLink(planId, link) {
  return writePlanPaths(planId, { [P.linkItem(link.id)]: link, [P.linkOrder()]: arrayUnion(link.id) });
}
export function setLinkProp(planId, id, key, value) {
  return writePlanPaths(planId, { [P.linkProp(id, key)]: value });
}
export function deleteLink(planId, id) {
  return writePlanPaths(planId, { [P.linkItem(id)]: deleteField(), [P.linkOrder()]: arrayRemove(id) });
}

/* ── dynasty allegiance (COARSE whole map — house-name keys) ───────────────── */
export function setAllegiance(planId, allegianceMap) {
  return writePlanPaths(planId, { [P.allegiance()]: allegianceMap });
}

/* ── beats (COARSE per act — beats are positional) ────────────────────────── */
export function setAct(planId, shape, ai, beats) {
  return writePlanPaths(planId, { [P.act(shape, ai)]: beats });
}
